"use client";

import type { ReactNode } from "react";
import {
  Controller,
  useFormContext,
  type ControllerProps,
} from "react-hook-form";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { CSSObject, SxProps, Theme } from "@mui/material/styles";
import { FormField } from "./FormField";

export type FormToggleButtonGroupOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
};

export type FormToggleButtonGroupProps = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
  required?: boolean;
  options: FormToggleButtonGroupOption[];
  /**
   * `"row"` lays the buttons out as flexible equal-width siblings on one
   * row. `"grid"` arranges them in a 2-column grid (used for the PHQ-9
   * answer set). `"column"` stacks buttons vertically, each full width.
   * Defaults to `"row"`.
   */
  layout?: "row" | "grid" | "column";
  /**
   * When `true`, only the selected button shows a border; unselected
   * buttons render with a transparent border (preserving layout). Hover
   * still indicates interactivity via background. Defaults to `false`.
   */
  borderSelectedOnly?: boolean;
  /**
   * When `true`, wraps the label, buttons, and helper text in a bordered
   * card. In the error state the card border and background switch to the
   * error palette while the buttons themselves stay unchanged. Defaults to
   * `false`.
   */
  bordered?: boolean;
  rules?: ControllerProps["rules"];
  disabled?: boolean;
};

const containerSx =
  (hasError: boolean): SxProps<Theme> =>
  (theme) => {
    const rmh = theme.vars!.palette.rmh;
    return {
      padding: theme.spacing(4),
      borderRadius: theme.shape.medium,
      borderStyle: "solid",
      borderWidth: theme.spacing(0.25),
      transition: theme.transitions.create(
        ["border-color", "background-color"],
        { duration: theme.transitions.duration.shortest }
      ),
      borderColor: hasError
        ? rmh.border["8-error"].active
        : rmh.border["1-surface"].rest,
      backgroundColor: hasError
        ? rmh.paper["9-errorFaint"].rest
        : rmh.paper["1-surface"].rest,
    };
  };

// Mirrors MuiButton's `light` variant (see MuiButton.ts → `primaryLight`).
// Inlined rather than driven by a theme override because future ToggleButton
// variants (e.g. body-figure picker) will need different palettes.
//
// All button styling is applied from the group's sx via `& .MuiToggleButton-root`
// — ToggleButtonGroup's default styled() rules target the same descendant
// selectors (`.MuiToggleButtonGroup-firstButton/middleButton/lastButton`) and
// would otherwise out-specify a sx prop placed on the child button.
const buildSx =
  (
    layout: "row" | "grid" | "column",
    borderSelectedOnly: boolean
  ): SxProps<Theme> =>
  (theme) => {
    const rmh = theme.vars!.palette.rmh;
    const restBorder = borderSelectedOnly
      ? "transparent"
      : rmh.border["4-faint"].rest;

    const buttonStyles: CSSObject = {
      backgroundColor: rmh.paper["1-surface"].rest,
      borderColor: restBorder,
      color: rmh.text.interactiveLink,
      "&:hover:not(.Mui-disabled, .Mui-selected)": {
        backgroundColor: rmh.paper["4-faint"].rest,
        borderColor: rmh.border["4-faint"].hover,
        color: rmh.text.interactiveLink,
      },
      "&:focus-visible": {
        backgroundColor: rmh.paper["4-faint"].rest,
        color: rmh.text.interactiveLink,
        boxShadow: rmh.focus.neutral,
      },
      "&:active:not(.Mui-disabled, .Mui-selected)": {
        backgroundColor: rmh.paper["4-faint"].active,
        borderColor: rmh.border["4-faint"].active,
        color: rmh.text.interactiveLink,
      },
      "&.Mui-selected, &.Mui-selected:hover": {
        backgroundColor: rmh.paper["7-brandFaint"].active,
        borderColor: rmh.border["7-brandFaint"].active,
        color: rmh.text.interactiveLink,
      },
      "&.Mui-disabled": {
        backgroundColor: rmh.stateBackground.disabledInput,
        borderColor: rmh.stateBorder.disabled,
        color: rmh.text.disabled,
      },
    };

    let layoutStyles: CSSObject = {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(2),
    };
    if (layout === "grid") {
      layoutStyles = {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: theme.spacing(2),
      };
    }
    if (layout === "column") {
      layoutStyles = {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
      };
    }

    // Column-stacked buttons must not flex-grow (that stretches them
    // vertically) — pin width to 100% and let height come from content.
    const isColumn = layout === "column";

    return {
      ...layoutStyles,
      width: "100%",
      // ToggleButtonGroup's segment-collapse rules target firstButton /
      // middleButton / lastButton with shorthand `border-left: 1px solid
      // transparent` and zeroed corner radii — at equal specificity, applying
      // the styling here via `& .MuiToggleButton-root` (sx after theme
      // defaults) lets our `borderColor`/`borderRadius`/`borderWidth`
      // shorthands win. Every button ends up fully bordered in row, grid,
      // and column layouts.
      "& .MuiToggleButton-root": {
        marginLeft: 0,
        marginTop: 0,
        flex: isColumn ? "0 0 auto" : 1,
        width: isColumn ? "100%" : undefined,
        minWidth: 0,
        textTransform: "none",
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        borderRadius: theme.shape.medium,
        borderStyle: "solid",
        borderWidth: theme.spacing(0.375),
        ...theme.typography.bodyMdMedium,
        transition: theme.transitions.create(
          ["background-color", "border-color", "color", "box-shadow"],
          { duration: theme.transitions.duration.shortest }
        ),
        ...buttonStyles,
      },
    };
  };

export const FormToggleButtonGroup = ({
  name,
  label,
  helperText,
  required,
  options,
  layout = "row",
  borderSelectedOnly = false,
  bordered = false,
  rules,
  disabled,
}: FormToggleButtonGroupProps) => {
  const { control } = useFormContext();
  const labelId = `${name}-label`;
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const hasError = !!fieldState.error;
        const field$ = (
          <FormField
            label={label}
            helperText={fieldState.error?.message ?? helperText}
            error={hasError}
            required={required}
            labelId={labelId}
          >
            <ToggleButtonGroup
              exclusive
              aria-labelledby={labelId}
              value={field.value ?? null}
              onChange={(_event, next) => {
                if (next === null) return;
                field.onChange(next);
              }}
              onBlur={field.onBlur}
              disabled={disabled}
              sx={buildSx(layout, borderSelectedOnly)}
            >
              {options.map((option, index) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  // RHF focuses the first button when the group fails
                  // validation, scrolling the group into view.
                  ref={index === 0 ? field.ref : undefined}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </FormField>
        );
        return bordered ? (
          <Box sx={containerSx(hasError)}>{field$}</Box>
        ) : (
          field$
        );
      }}
    />
  );
};
